import { inject, Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Auth0SessionService } from './auth0-session.service';

/** Exact configured resource boundaries; provider/third-party URLs receive no Shine bearer. */
export function isShineResource(value: string, bases: readonly string[]): boolean {
  const request = new URL(value, window.location.origin);
  if (request.username || request.password) return false;
  return bases.some(value => {
    const base = new URL(value);
    const path = base.pathname.endsWith('/') ? base.pathname : `${base.pathname}/`;
    return request.origin === base.origin && (request.pathname === base.pathname || request.pathname.startsWith(path));
  });
}

@Injectable()
export class Auth0Interceptor implements HttpInterceptor {
  private readonly sessions = inject(Auth0SessionService);
  private readonly bases = [environment.authBaseServerUrl, environment.aiBaseServerUrl,
    environment.aiWebServerUrl, environment.enrichBaseServerUrl, environment.connectBaseServerUrl,
    environment.webinarsServerUrl, environment.translationsBaseServerUrl];

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!isShineResource(req.url, this.bases)) return next.handle(req);
    return from(this.sessions.ensureSession()).pipe(switchMap(session => {
      if (!session) return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Sign-in required' }));
      const authenticated = req.clone({ setHeaders: { Authorization: `Bearer ${session.accessToken}` } });
      return next.handle(authenticated).pipe(catchError(error => {
        // Retry safe reads once after coalesced rotation; never replay a mutation.
        if (!(error instanceof HttpErrorResponse) || error.status !== 401 || !['GET', 'HEAD'].includes(req.method)) {
          return throwError(() => error);
        }
        return from(this.sessions.ensureSession(true)).pipe(switchMap(refreshed => refreshed
          ? next.handle(req.clone({ setHeaders: { Authorization: `Bearer ${refreshed.accessToken}` } }))
          : throwError(() => error)));
      }));
    }));
  }
}
